import { useMemo } from "react";
import BUILDKITE_EMOJI from "~/emoji/buildkite";
import UNICODE_EMOJI from "~/emoji/apple";

// Type definitions
export type EmojiData = {
  name: string;
  unicode?: string;
  image?: string;
  aliases?: string[];
  category?: string;
};

export type ProcessedEmoji = EmojiData & {
  source: "unicode" | "buildkite";
  searchTerms: string[];
};

export type EmojiSource = {
  emoji: EmojiData[];
  index: Record<string, number>;
  host: string;
};

/**
 * Processes emoji data from both Buildkite and Unicode sources into a unified format.
 * Deduplicates emojis (preferring Buildkite versions over Unicode) and generates search terms.
 *
 * @param buildkiteEmoji - Buildkite emoji data source (defaults to global BUILDKITE_EMOJI)
 * @param unicodeEmoji - Unicode emoji data source (defaults to global UNICODE_EMOJI)
 * @returns Array of ProcessedEmoji objects with unified format and search terms
 */
export const processEmojiData = (
  buildkiteEmoji = BUILDKITE_EMOJI,
  unicodeEmoji = UNICODE_EMOJI,
): ProcessedEmoji[] => {
  const emojiMap = new Map<string, ProcessedEmoji>();

  // Process Buildkite emojis
  if (buildkiteEmoji?.emoji && buildkiteEmoji?.index) {
    Object.entries(buildkiteEmoji.index).forEach(([key, index]) => {
      const emojiData = buildkiteEmoji.emoji[index as number];
      if (emojiData) {
        const name = key.replace(/^:/, "").replace(/:$/, "");
        const uniqueKey = `${emojiData.image || emojiData.unicode}_buildkite`;

        if (!emojiMap.has(uniqueKey)) {
          emojiMap.set(uniqueKey, {
            ...emojiData,
            name,
            source: "buildkite",
            searchTerms: [
              name.toLowerCase(),
              ...(emojiData.aliases || []).map((alias) => alias.toLowerCase()),
            ],
          });
        } else {
          const existing = emojiMap.get(uniqueKey) || {
            searchTerms: [],
            name: "",
            source: "buildkite" as const,
          };
          existing.searchTerms.push(name.toLowerCase());
          existing.searchTerms.push(
            ...(emojiData.aliases || []).map((alias) => alias.toLowerCase()),
          );
          existing.searchTerms = Array.from(new Set(existing.searchTerms));
        }
      }
    });
  }

  // Process Unicode emojis
  if (unicodeEmoji?.emoji && unicodeEmoji?.index) {
    Object.entries(unicodeEmoji.index).forEach(([key, index]) => {
      if (key.includes("skin-tone")) {
        return;
      }
      const emojiData = unicodeEmoji.emoji[index as number];
      if (emojiData && !key.includes("::")) {
        const name = key.replace(/^:/, "").replace(/:$/, "");
        const uniqueKey = `${emojiData.image || emojiData.unicode}_unicode`;
        const buildkiteKey = `${emojiData.image || emojiData.unicode}_buildkite`;

        if (!emojiMap.has(buildkiteKey)) {
          if (!emojiMap.has(uniqueKey)) {
            emojiMap.set(uniqueKey, {
              ...emojiData,
              name,
              source: "unicode",
              searchTerms: [
                name.toLowerCase(),
                ...(emojiData.aliases || []).map((alias) => alias.toLowerCase()),
              ],
            });
          } else {
            const existing = emojiMap.get(uniqueKey) || {
              searchTerms: [],
              name: "",
              source: "unicode" as const,
            };
            existing.searchTerms.push(name.toLowerCase());
            existing.searchTerms.push(
              ...(emojiData.aliases || []).map((alias) => alias.toLowerCase()),
            );
            existing.searchTerms = Array.from(new Set(existing.searchTerms));
          }
        }
      }
    });
  }

  return Array.from(emojiMap.values());
};

/**
 * Gets the full URL for an emoji image based on its source.
 *
 * @param emoji - The processed emoji object
 * @param buildkiteEmoji - Buildkite emoji data source (defaults to global BUILDKITE_EMOJI)
 * @param unicodeEmoji - Unicode emoji data source (defaults to global UNICODE_EMOJI)
 * @returns Full URL string for the emoji image
 */
export const getEmojiSrc = (
  emoji: ProcessedEmoji,
  buildkiteEmoji = BUILDKITE_EMOJI,
  unicodeEmoji = UNICODE_EMOJI,
): string => {
  return `${emoji.source === "buildkite" ? buildkiteEmoji.host : unicodeEmoji.host}${emoji.image}`;
};

/**
 * Finds an emoji by its name from a list of processed emojis.
 * Automatically strips colons from the name (e.g., ":smile:" becomes "smile").
 *
 * @param emojis - Array of processed emojis to search through
 * @param name - Emoji name to find (with or without surrounding colons)
 * @returns The matching ProcessedEmoji object, or null if not found
 */
export const findEmojiByName = (emojis: ProcessedEmoji[], name: string): ProcessedEmoji | null => {
  const normalizedName = name.replace(/^:/, "").replace(/:$/, "");
  return emojis.find((emoji) => emoji.name === normalizedName) || null;
};

/**
 * Searches through emojis by name and aliases using a text query.
 * Returns all emojis if query is empty, otherwise filters by partial matches.
 *
 * @param emojis - Array of processed emojis to search through
 * @param query - Search query string (case-insensitive)
 * @returns Filtered array of ProcessedEmoji objects that match the query
 */
export const searchEmojis = (emojis: ProcessedEmoji[], query: string): ProcessedEmoji[] => {
  if (!query.trim()) {
    return emojis;
  }

  const normalizedQuery = query.toLowerCase().trim();
  return emojis.filter((emoji) => emoji.searchTerms.some((term) => term.includes(normalizedQuery)));
};

/**
 * Hook that returns processed emoji data, memoized for performance.
 * Combines and processes both Buildkite and Unicode emojis into a unified format.
 *
 * @param buildkiteEmoji - Buildkite emoji data source (defaults to global BUILDKITE_EMOJI)
 * @param unicodeEmoji - Unicode emoji data source (defaults to global UNICODE_EMOJI)
 * @returns Memoized array of ProcessedEmoji objects
 */
export const useProcessedEmojis = (
  buildkiteEmoji = BUILDKITE_EMOJI,
  unicodeEmoji = UNICODE_EMOJI,
) => {
  return useMemo(
    () => processEmojiData(buildkiteEmoji, unicodeEmoji),
    [buildkiteEmoji, unicodeEmoji],
  );
};
